import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Files } from 'lucide-react';
import { Button } from '@roger/ui-components';
import { useStore } from '../../store';
import { PermissionSlug } from '../../constants/featureFlags';
import { listPatientDuplicateImports } from '../../services/api';

/** Links a patient to the imported copies made beside it.
 *
 *  A plain import that finds this patient already here builds a second chart
 *  instead of refusing. The lookup is an importer read, so the notice is
 *  shown only to users with importer access, and it asks nothing otherwise. */
export const DuplicateImportNotice: React.FC<{ patientId: string }> = ({
  patientId,
}) => {
  const hasImporterAccess = useStore(state =>
    state.hasPermission(PermissionSlug.IMPORTER_ACCESS)
  );
  const [copyIds, setCopyIds] = useState<string[]>([]);

  useEffect(() => {
    // Reset so a previous patient's copies never linger when patientId changes
    // or access is lost.
    setCopyIds([]);
    if (!patientId || !hasImporterAccess) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await listPatientDuplicateImports({
          path: { patient_id: patientId },
        });
        if (cancelled) return;
        setCopyIds(
          (res?.data?.duplicate_imports ?? []).map(
            link => link.duplicate_patient_id
          )
        );
      } catch (err) {
        console.error('Error fetching imported copies:', err);
        if (!cancelled) setCopyIds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, hasImporterAccess]);

  if (!hasImporterAccess || copyIds.length === 0) return null;

  const single = copyIds.length === 1;
  return (
    <div
      className='flex flex-col gap-2 border-b border-blue-200 bg-blue-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between'
      data-testid='duplicate-import-notice'
    >
      <span className='flex items-center gap-2 text-sm text-blue-900'>
        <Files className='h-4 w-4 shrink-0' aria-hidden='true' />
        {single
          ? 'This patient also has an imported chart from the External EMR.'
          : `This patient also has ${copyIds.length} imported charts from the External EMR.`}
      </span>
      <div className='flex flex-wrap gap-2'>
        {copyIds.map((copyId, index) => (
          <Button
            key={copyId}
            asChild
            type='button'
            variant='outline'
            size='sm'
          >
            <Link to={`/patients/${copyId}`}>
              {single
                ? 'Open imported chart'
                : `Open imported chart ${index + 1}`}
            </Link>
          </Button>
        ))}
      </div>
    </div>
  );
};
